import type { BehaviorNode } from "./types.js";
import type { BehaviorGraph } from "../layout/types.js";

/**
 * Returns whether a trigger graph already contains an `eventOutput` terminal node.
 */
export function triggerGraphHasEventOutput(
  graph: Pick<BehaviorGraph, "nodes"> | undefined,
): boolean {
  if (!graph) return false;
  return graph.nodes.some(
    (node) => node.type === "control" && node.controlType === "eventOutput",
  );
}

/**
 * A custom event is publishable in the scene catalog only when its trigger graph
 * contains an `eventOutput` node with at least one incoming flow connection.
 */
export function isCustomEventTriggerGraphPublishable(
  graph: Pick<BehaviorGraph, "nodes" | "connections"> | undefined,
): boolean {
  if (!graph) return false;
  const outputNode = graph.nodes.find(
    (node) => node.type === "control" && node.controlType === "eventOutput",
  );
  if (!outputNode) return false;
  return graph.connections.some(
    (connection) =>
      connection.toNodeId === outputNode.id && connection.toPortId === "in",
  );
}

/**
 * Builds the default trigger graph for a newly created custom scene event:
 * a single `eventOutput` node placed on the right side, vertically centered.
 *
 * @param options.canvasWidth - Visible behavior canvas width in world coordinates.
 * @param options.canvasHeight - Visible behavior canvas height in world coordinates.
 */
export function createDefaultCustomEventTriggerGraph(options: {
  canvasWidth: number;
  canvasHeight: number;
  nodeId?: string;
  flowInPortLabel?: string;
}): BehaviorGraph {
  const canvasWidth = Math.max(1, options.canvasWidth);
  const canvasHeight = Math.max(1, options.canvasHeight);
  const nodeId = options.nodeId ?? crypto.randomUUID();
  const x = Math.max(0, canvasWidth - 320);
  const y = Math.max(0, Math.round(canvasHeight / 2 - 50));

  const eventOutputNode: BehaviorNode = {
    id: nodeId,
    x,
    y,
    type: "control",
    controlType: "eventOutput",
    dataValueType: "flow",
    ports: [
      {
        id: "in",
        label: options.flowInPortLabel ?? "",
        direction: "input",
        valueType: "flow",
      },
    ],
  };

  return {
    nodes: [eventOutputNode],
    connections: [],
  };
}

/**
 * Returns whether a behavior node references the custom event currently being edited.
 */
export function isSelfReferencingCustomSceneEventNode(
  node: BehaviorNode,
  editingCustomEventId: string | undefined,
): boolean {
  return (
    Boolean(editingCustomEventId) &&
    node.type === "event" &&
    node.kind === "onCustomSceneEvent" &&
    node.customEventId === editingCustomEventId
  );
}
